import { useMemo } from 'react';
import { ethers } from 'ethers';
import { useAbstractClient } from '@abstract-foundation/agw-react';
import { NOOT_TOKEN_ADDRESS, LADDER_CONTRACT_ADDRESS, RPC_URL } from './utils/consts';
import { privateKeyToAccount } from 'viem/accounts';
import { useAccount } from 'wagmi';
// Standard ERC20 ABI for balanceOf and approve functions
const ERC20_ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)"
];

export const useTokenContract = (sessionData, createSession) => {
  const { data: agwClient } = useAbstractClient();
  const { address } = useAccount();

  const provider = useMemo(() =>
    new ethers.JsonRpcProvider(RPC_URL),
    []
  );

  const tokenContract = useMemo(
    () => new ethers.Contract(NOOT_TOKEN_ADDRESS, ERC20_ABI, provider),
    [provider]
  );

  async function getClient() {
    if (!agwClient) throw new Error('Abstract client not initialized');
    
    // Use the provided session or create a new one
    let session = sessionData;
    if (!session && createSession) {
      try {
        session = await createSession();
      } catch (error) {
        console.error("Failed to create session:", error);
      }
    }
    
    return session && session.privateKey && session.session
      ? agwClient.toSessionClient(privateKeyToAccount(session.privateKey), session.session)
      : agwClient;
  }

  const checkBalance = async (address) => {
    try {
      const balance = await tokenContract.balanceOf(address);
      return balance;
    } catch (error) {
      console.error("Error checking balance:", error);
      throw new Error("Failed to check token balance");
    }
  };

  const checkAllowance = async (ownerAddress) => {
    try {
      const allowance = await tokenContract.allowance(ownerAddress, LADDER_CONTRACT_ADDRESS);
      return allowance;
    } catch (error) {
      console.error("Error checking allowance:", error);
      throw new Error("Failed to check token allowance");
    }
  };

  const approveTokens = async (amount) => {
    try {
      const amountInWei = ethers.parseEther(amount.toString());
      //const client = await getClient();
      const client = agwClient; // for some reason sessionData is not working on approval

      console.log("Approving tokens:", amountInWei);
      // lets double check balance and log it
      const balance = await tokenContract.balanceOf(address);
      console.log("Balance:", balance);
      
      // Check if user has enough tokens before trying to approve
      if (balance < amountInWei) {
        throw new Error(`Insufficient token balance. You have ${ethers.formatEther(balance)} NOOT, but trying to approve ${amount} NOOT.`);
      }
      
      return client.sendTransaction({
        to: NOOT_TOKEN_ADDRESS,
        data: tokenContract.interface.encodeFunctionData('approve', [LADDER_CONTRACT_ADDRESS, amountInWei])
      });
    } catch (error) {
      console.error("Error approving tokens:", error);
      throw new Error("Failed to approve tokens");
    }
  };

  return {
    tokenContract,
    checkBalance,
    checkAllowance,
    approveTokens
  };
}; 